// Client-side mirror of UpdateCompanyValidator in legacy-api. The backend stays the
// source of truth (e.g. org number checksums per country and site path uniqueness),
// its field errors are mapped back onto the form via mapServerErrors.

export interface CompanyFormValues {
    Name: string;
    OrganisationNumber: string;
    TypeId: string;
    CategoryId: string;
    Phone: string;
    Email: string;
    SitePath: string;
    Homepage: string;
    Details: string;
    Street1: string;
    Street2: string;
    ZipCode: string;
    City: string;
    CountryId: string;
    Latitude: string;
    Longitude: string;
}

export type CompanyFormErrors = Partial<Record<keyof CompanyFormValues, string>>;

// TypeId 2 = personal use, 3 = other; these skip the org number format check server side
const TYPES_WITHOUT_ORG_NUMBER_CHECK = ['2', '3'];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[0-9\s\-()]{6,20}$/;
const ORG_NUMBER_REGEX = /^[0-9\s\-]+$/;
const SITE_PATH_FORBIDDEN = /[*&#?\s/]/;

const isAbsoluteHttpUrl = (value: string) => {
    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
};

const validateCoordinate = (value: string, max: number) => {
    if (!value?.toString().trim()) return undefined;
    const number = Number(value);
    if (Number.isNaN(number)) return 'Must be a number';
    if (number < -max || number > max) return `Must be between -${max} and ${max}`;
    return undefined;
};

export const validateCompany = (values: CompanyFormValues): CompanyFormErrors => {
    const errors: CompanyFormErrors = {};
    const trim = (v?: string | number | null) => (v ?? '').toString().trim();

    const name = trim(values.Name);
    if (!name) {
        errors.Name = 'Name is required';
    } else if (name.length > 80) {
        errors.Name = 'Name can be at most 80 characters';
    }

    const orgNumber = trim(values.OrganisationNumber);
    if (!orgNumber) {
        errors.OrganisationNumber = 'Organisation number is required';
    } else if (
        !TYPES_WITHOUT_ORG_NUMBER_CHECK.includes(trim(values.TypeId)) &&
        !ORG_NUMBER_REGEX.test(orgNumber)
    ) {
        errors.OrganisationNumber = 'Organisation number can only contain digits and dashes';
    }

    if (!trim(values.TypeId)) {
        errors.TypeId = 'Type is required';
    }

    if (!trim(values.CategoryId)) {
        errors.CategoryId = 'Category is required';
    }

    const email = (values.Email ?? '').trim();
    if (!email) {
        errors.Email = 'Email is required';
    } else if (!EMAIL_REGEX.test(email)) {
        errors.Email = 'Email is not valid';
    }

    const phone = trim(values.Phone);
    if (phone && !PHONE_REGEX.test(phone)) {
        errors.Phone = 'Phone number is not valid';
    }

    const sitePath = values.SitePath ?? '';
    if (!sitePath.trim()) {
        errors.SitePath = 'Booking homepage is required';
    } else if (SITE_PATH_FORBIDDEN.test(sitePath)) {
        errors.SitePath = 'Booking homepage cannot contain spaces, slashes or * & # ?';
    } else if (/[A-Z]/.test(sitePath)) {
        errors.SitePath = 'Booking homepage cannot contain upper case characters';
    }

    const homepage = trim(values.Homepage);
    if (homepage && !isAbsoluteHttpUrl(homepage)) {
        errors.Homepage = 'Homepage must be a full url starting with http:// or https://';
    }

    const latitudeError = validateCoordinate(values.Latitude, 90);
    if (latitudeError) errors.Latitude = latitudeError;

    const longitudeError = validateCoordinate(values.Longitude, 180);
    if (longitudeError) errors.Longitude = longitudeError;

    return errors;
};

// ServiceStack returns validation failures as ResponseStatus.Errors with a FieldName
export const mapServerErrors = (err: any): CompanyFormErrors => {
    const serverErrors: { FieldName?: string; Message?: string }[] =
        err?.data?.ResponseStatus?.Errors ?? [];

    return serverErrors.reduce<CompanyFormErrors>((acc, e) => {
        if (e.FieldName && e.Message) {
            acc[e.FieldName as keyof CompanyFormValues] = e.Message;
        }
        return acc;
    }, {});
};
